import { useState } from "react";

import { useResendVerificationEmailMutation } from "@/apollo/types";
import { SecondaryButton, UtilityButton } from "@/components/elements/Button";
import Center from "@/components/elements/layout/Center";
import { InfoIcon } from "@/components/icons/graphic";
import { useCountdown } from "@/hooks/useCountdown";
import { useTimeout } from "@/hooks/useTimeout";
import SimplePageLayout from "@/layouts/SimplePageLayout";
import { usePersistUTMParamsTemporarily } from "@/pages/Setup/hooks/useUTMSourceTempStorage";
import { useToast } from "@/providers/ToastProvider";
import { useAuth0 } from "@auth0/auth0-react";

import { MailIcon } from "lucide-react";
import PageContainer from "../../components/PageContainer";
import { Heading } from "../../components/Typography";

function useThrottledAction<T>(
  action: () => T | Promise<T>,
  throttleDurationMs: number,
) {
  const [isThrottled, setIsThrottled] = useState(false);

  const [countdown, { startCountdown, resetCountdown }] = useCountdown({
    countStart: throttleDurationMs / 1000,
    intervalMs: 1000,
  });

  const { reset } = useTimeout(() => {
    setIsThrottled(false);
  }, throttleDurationMs);

  const performAction = async () => {
    if (isThrottled) return;
    setIsThrottled(true);
    try {
      await Promise.resolve(action());
    } catch {
      // ignored
    } finally {
      resetCountdown();
      startCountdown();
      reset();
    }
  };

  return [
    performAction,
    {
      countdown,
      isThrottled,
    },
  ] as const;
}

export default function EmailNotVerifiedPage() {
  const { logout } = useAuth0();
  const toast = useToast();

  const searchParams = new URLSearchParams(window.location.search);
  usePersistUTMParamsTemporarily(searchParams);

  const [resendVerificationEmail, { loading: isResending }] =
    useResendVerificationEmailMutation({
      onCompleted: () => {
        toast("Sent. Check your inbox.", "", "success");
      },
      onError: () => {
        toast(
          "Error resending verification email",
          "Please try again later.",
          "error",
        );
      },
    });

  const handleContinue = () => {
    window.location.href = window.location.origin + "?" + searchParams;
  };

  const handleCancel = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const [handleResend, { countdown, isThrottled }] = useThrottledAction(
    () => resendVerificationEmail(),
    30_000,
  );

  return (
    <SimplePageLayout>
      <Center className="h-full">
        <PageContainer size="sm">
          <InfoIcon className="mb-5 w-12" />
          <Heading className="mb-6">Welcome</Heading>
          <div className="text-center text-sm leading-relaxed text-gray-600 dark:text-gray-300">
            <p>Check your email and click the verification link.</p>
            <p className="mt-6 text-muted-foreground">
              Can’t find it? Check spam/junk (or Gmail’s Promotions) or resend
              the email.
            </p>
          </div>
          <div className="mt-8 flex flex-col items-center space-y-8">
            <SecondaryButton
              onClick={handleResend}
              isLoading={isResending}
              loadingText="Resending..."
              disabled={isThrottled || isResending}
              icon={<MailIcon />}
            >
              {isThrottled ? `Resend email (${countdown}s)` : "Resend email"}
            </SecondaryButton>
            <div className="flex items-center gap-8">
              <UtilityButton onClick={handleContinue}>
                Already verified? Continue here
              </UtilityButton>
              <UtilityButton className="p-2" onClick={handleCancel}>
                Cancel
              </UtilityButton>
            </div>
          </div>
        </PageContainer>
      </Center>
    </SimplePageLayout>
  );
}
